'use client';

import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardAction, CardFooter, CardHeader } from '@/components/ui/card';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

interface EnhancedMetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  description?: string;
  icon: React.ElementType;
  /** When provided, the entire card renders as a single accessible link. */
  to?: string;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'neutral';
  };
  progress?: {
    value: number; // percentage
    target?: number;
  };
  gradient?: string;
  isLoading?: boolean;
  disabled?: boolean;
  // Allow callers to pass style/variant hints used by some views
  accentClass?: string;
  variant?: string;
}

function CountUpAnimation({
  end,
  duration = 2000,
  prefix = '',
  suffix = '',
}: {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
}) {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;

      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - (1 - progress) ** 4;

      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration]);

  return (
    <span>
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>
  );
}

function ProgressRing({
  value,
  size = 40,
  strokeWidth = 3,
  color = 'hsl(var(--primary))',
}: {
  value: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="relative">
      <svg width={size} height={size} className="transform -rotate-90">
        <title>Progress Ring</title>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          className="text-muted-foreground/20"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-1000 ease-in-out"
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold tabular-nums">{Math.round(value)}%</span>
      </div>
    </div>
  );
}

export function EnhancedMetricCard({
  title,
  value,
  subtitle,
  description,
  icon: Icon,
  to,
  trend,
  progress,
  gradient = 'from-neutral-0 to-neutral-100 dark:from-neutral-900/60 dark:to-neutral-800/60',
  isLoading = false,
  disabled = false,
}: EnhancedMetricCardProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const glowRef = React.useRef<HTMLDivElement>(null);

  const handleMouseMove = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const el = glowRef.current;
    if (!el) return;
    const rect = e.currentTarget.getBoundingClientRect();
    el.style.setProperty('--glow-x', `${e.clientX - rect.left}px`);
    el.style.setProperty('--glow-y', `${e.clientY - rect.top}px`);
  }, []);

  React.useEffect(() => {
    setIsVisible(true);
  }, []);

  const TrendIcon =
    trend?.direction === 'up' ? TrendingUp : trend?.direction === 'down' ? TrendingDown : Minus;

  const trendColor =
    trend?.direction === 'up'
      ? 'text-green-700 dark:text-green-400'
      : trend?.direction === 'down'
        ? 'text-red-600 dark:text-red-400'
        : 'text-muted-foreground';

  if (isLoading) {
    return (
      <Card
        className={`@container/card bg-gradient-to-br ${gradient} border border-neutral-200/80 dark:border-neutral-700/60 shadow-none animate-pulse`}
      >
        <CardHeader className="mb-1 pb-1">
          <div className="h-4 bg-gray-300 rounded w-3/4 mb-2" />
          <div className="h-8 bg-gray-300 rounded w-1/2" />
          <CardAction className="self-start">
            <div className="w-10 h-10 bg-gray-300 rounded-sm" />
          </CardAction>
        </CardHeader>
        <CardFooter className="pt-0">
          <div className="h-3 bg-gray-300 rounded w-full" />
        </CardFooter>
      </Card>
    );
  }

  const isValueNaN =
    disabled ||
    (typeof value === 'string' && value.trim().toUpperCase() === 'NAN') ||
    (typeof value === 'number' && Number.isNaN(value));

  const numericValue =
    typeof value === 'string' ? parseFloat(value.replace(/[^0-9.]/g, '')) || 0 : value;
  const shouldAnimateCurrency =
    typeof value === 'string' &&
    value.includes('$') &&
    !value.includes('M') &&
    !value.includes('K') &&
    !value.includes('.');

  const isClickable = Boolean(to) && !isValueNaN;

  const card = (
    <Card
      className={cn(
        `@container/card bg-gradient-to-br ${gradient} border border-neutral-200/80 dark:border-neutral-700/60 shadow-none transition-all duration-300`,
        // Make gradients feel "alive" by animating background position.
        // (background-position only shows movement if background-size > 100%)
        'bg-[length:200%_200%] bg-[position:0%_0%]',
        'relative overflow-hidden',
        isVisible && 'animate-in slide-in-from-bottom-4 duration-500',
        // Not faded with opacity, which would take the card's text below 4.5:1; the
        // value is muted instead.
        isValueNaN && 'grayscale pointer-events-none',
        isClickable &&
          cn(
            'cursor-pointer',
            'transition-[background-position,border-color,filter] duration-700 ease-out',
            'group-hover:bg-[position:100%_100%] group-focus-visible:bg-[position:100%_100%]',
            'group-hover:border-sky-200/80 dark:group-hover:border-sky-500/40',
            'group-focus-visible:border-sky-200/80 dark:group-focus-visible:border-sky-500/40',
            'group-hover:brightness-102 group-focus-visible:brightness-102'
          )
      )}
    >
      {isClickable && (
        <div
          ref={glowRef}
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-500 ease-out',
            'group-hover:opacity-100 group-focus-visible:opacity-100'
          )}
          style={{
            background:
              'radial-gradient(320px circle at var(--glow-x, 50%) var(--glow-y, 50%), rgba(56, 189, 248, 0.14), rgba(6, 182, 212, 0.04) 50%, transparent 100%)',
          }}
        />
      )}
      <CardHeader className="relative z-10 mb-1 pb-1">
        <Typography variant="metric-title" className="font-medium text-foreground/70">
          {title}
        </Typography>
        <Typography
          variant="metric-value"
          className="@[250px]/card:text-3xl flex items-center gap-2"
        >
          {isValueNaN ? (
            <span className="text-muted-foreground">NaN</span>
          ) : typeof value === 'string' && value.includes('$') ? (
            shouldAnimateCurrency ? (
              <CountUpAnimation
                end={numericValue}
                prefix="$"
                suffix={value.includes('M') ? 'M' : value.includes('K') ? 'K' : ''}
              />
            ) : (
              value
            )
          ) : typeof value === 'number' ? (
            <CountUpAnimation end={value} />
          ) : (
            value
          )}
          {!isValueNaN && progress && (
            <ProgressRing value={progress.value} color="hsl(var(--primary))" />
          )}
        </Typography>
        <CardAction className="self-start">
          <Icon className="w-5 h-5 text-primary transition-transform" />
        </CardAction>
      </CardHeader>
      <CardFooter className="relative z-10 flex-col items-start gap-2 text-sm pt-0">
        {!isValueNaN && trend && (
          <div className={cn('flex items-center gap-1 font-medium', trendColor)}>
            <TrendIcon className="h-3 w-3" />
            <span className="tabular-nums">{Math.abs(trend.value)}%</span>
            <span className="text-muted-foreground">vs last month</span>
          </div>
        )}
        {subtitle && (
          <Typography
            variant="metric-subtitle"
            className="flex items-center gap-2 font-medium text-foreground/80"
          >
            {subtitle}
          </Typography>
        )}
        {description && (
          <Typography variant="body-sm" className="text-muted-foreground leading-relaxed">
            {description}
          </Typography>
        )}
        {!isValueNaN && progress?.target && (
          <div className="w-full">
            <div className="flex justify-between mb-1">
              <Typography variant="caption">Progress</Typography>
              <Typography variant="caption" className="tabular-nums">
                {progress.value}% of {progress.target}
              </Typography>
            </div>
            <div className="w-full bg-muted-foreground/20 rounded-full h-1.5">
              <div
                className="h-1.5 bg-primary rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${Math.min(progress.value, 100)}%` }}
              />
            </div>
          </div>
        )}
      </CardFooter>
    </Card>
  );

  if (!isClickable || !to) return card;

  return (
    <Link
      to={to}
      onMouseMove={handleMouseMove}
      aria-label={`View ${title}`}
      className={cn(
        'group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'rounded-[inherit]'
      )}
    >
      {card}
    </Link>
  );
}
